import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, ArrowLeft, FileWarning } from 'lucide-react'
import { getDraftFile, putDraftFile } from '@/api/draftFiles'
import { deleteDraftPrompt, getDraftPrompt, putDraftPrompt } from '@/api/prompts'
import { Banner } from '@/components/Banner'
import { STALE_DRAFT_SAVE_REASON, StaleDraftBanner } from '@/components/StaleDraftBanner'
import { EmptyState } from '@/components/EmptyState'
import { InfoTooltip } from '@/components/InfoTooltip'
import { Tooltip } from '@/components/Tooltip'
import { useServerSeed } from '@/hooks/useServerSeed'
import { errorMessage, extractFieldErrors, formatSaveError } from '@/lib/apiErrors'
import { refreshAfterWrite } from '@/lib/refreshAfterWrite'
import type {
  DocumentTypesFile,
  ExtractorsFile,
  GlobalDefaultsFile,
  ProductType,
  ProductTypesFile,
} from '@/types/bundle'
import { AddDocumentTypeModal } from './components/AddDocumentTypeModal'
import { CaseDataRequirementsSection } from './components/CaseDataRequirementsSection'
import { DocumentRequirementsSection } from './components/DocumentRequirementsSection'
import { ExtractionSection } from './components/ExtractionSection'
import { LimitsSection } from './components/LimitsSection'
import { OrchestrationStepsSection } from './components/OrchestrationStepsSection'
import { OutputResolutionSection } from './components/OutputResolutionSection'
import { deriveProductBaselinePromptFilename } from './constants'
import { resolveOutputResolutionForSave } from './outputResolutionUtils'

type LimitField = 'max_documents' | 'max_total_pages' | 'max_pdf_size_mb'

interface LocationState {
  initialEntry?: ProductType
  /** Set only by Duplicate, when the source product had a Baseline Prompt -
   * fetched eagerly by the list screen so the new (unsaved) form shows it
   * immediately; only written to a freshly-derived file on Save. */
  initialBaselinePromptContent?: string
}

function SkeletonBlock() {
  return <div className="h-96 animate-pulse rounded-control bg-surface-raised" />
}

const PRODUCT_TYPES_QUERY_KEY = ['draft-file', 'product_types']

export function ProductTypeDetailScreen() {
  const { productId = '' } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const initialEntry = (location.state as LocationState | null)?.initialEntry
  const initialBaselinePromptContent = (location.state as LocationState | null)?.initialBaselinePromptContent

  const productTypesQuery = useQuery({
    queryKey: PRODUCT_TYPES_QUERY_KEY,
    queryFn: () => getDraftFile<ProductTypesFile>('product_types'),
  })
  const globalDefaultsQuery = useQuery({
    queryKey: ['draft-file', 'global_defaults'],
    queryFn: () => getDraftFile<GlobalDefaultsFile>('global_defaults'),
  })
  const documentTypesQuery = useQuery({
    queryKey: ['draft-file', 'document_types'],
    queryFn: () => getDraftFile<DocumentTypesFile>('document_types'),
  })
  const extractorsQuery = useQuery({
    queryKey: ['draft-file', 'extractors'],
    queryFn: () => getDraftFile<ExtractorsFile>('extractors'),
  })

  const [draft, setDraft] = useState<ProductTypesFile | null>(null)
  const [savedSnapshot, setSavedSnapshot] = useState<ProductTypesFile | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  // Baseline Prompt content lives in a separate prompts/*.txt file (PRD 29),
  // never inside product_types.json itself - tracked here, outside `draft`,
  // for that reason. null = "not yet initialized from a query/seed".
  const [baselinePrompt, setBaselinePrompt] = useState('')
  const [savedBaselinePrompt, setSavedBaselinePrompt] = useState<string | null>(null)
  const [baselinePromptError, setBaselinePromptError] = useState<string | null>(null)

  const existingProductForQuery = productTypesQuery.data?.product_types[productId]
  const baselineFilename = existingProductForQuery?.extraction?.prompt_template_file
  const baselinePromptQuery = useQuery({
    queryKey: ['draft-prompt', baselineFilename],
    queryFn: () => getDraftPrompt(baselineFilename ?? ''),
    enabled: Boolean(baselineFilename),
  })
  const currentExtractorId = draft?.product_types[productId]?.extraction?.extractor_id
  const extractorDefaultFilename = currentExtractorId
    ? extractorsQuery.data?.extractors[currentExtractorId]?.prompt_template_file
    : undefined
  const extractorDefaultQuery = useQuery({
    queryKey: ['draft-prompt', extractorDefaultFilename],
    queryFn: () => getDraftPrompt(extractorDefaultFilename ?? ''),
    enabled: Boolean(extractorDefaultFilename),
  })
  // Pending name for any synthetic (unsaved new/duplicated) output_resolution
  // entry - lives here, not in OutputResolutionSection, since Save (also
  // here) needs it to resolve the final keys. Mirrors document-types'
  // pattern of storing the draft identifier on the field object itself;
  // output_resolution entries have no such property to piggyback on.
  const [outputKeyDrafts, setOutputKeyDrafts] = useState<Record<string, string>>({})

  function screenIsDirty(): boolean {
    return (
      (savedSnapshot !== null && JSON.stringify(draft) !== JSON.stringify(savedSnapshot)) ||
      (savedBaselinePrompt !== null && baselinePrompt !== savedBaselinePrompt)
    )
  }

  // Seeds the editable draft, and re-seeds it when the server copy changes
  // (hooks/useServerSeed.ts). For a brand-new/duplicated product type
  // (absent from the file, initialEntry supplied via the list screen's
  // New/Duplicate modal), synthesizes the draft by inserting it client-side -
  // a real unsaved draft, never written to the backend until Save. Duplicate
  // seeds the new form with the source's Baseline Prompt text, written to a
  // freshly-derived file only when this product is actually saved (Design
  // PRD 10's Duplicate bullet), never left pointing at the source's own file.
  const configSeed = useServerSeed(PRODUCT_TYPES_QUERY_KEY, productTypesQuery, {
    seed: (data) => {
      const existing = data.product_types[productId]
      if (existing) {
        setDraft(data)
        setSavedSnapshot(data)
        if (!existing.extraction?.prompt_template_file) {
          setBaselinePrompt('')
          setSavedBaselinePrompt('')
        }
      } else if (initialEntry) {
        setDraft({ ...data, product_types: { ...data.product_types, [productId]: initialEntry } })
        setSavedSnapshot(data)
        setBaselinePrompt(initialBaselinePromptContent ?? '')
        setSavedBaselinePrompt('')
      }
    },
    isDirty: screenIsDirty,
  })
  // An existing product's Baseline Prompt, from its own draft-or-live
  // prompt file, followed the same way.
  const promptSeed = useServerSeed(['draft-prompt', baselineFilename], baselinePromptQuery, {
    seed: (data) => {
      setBaselinePrompt(data.content)
      setSavedBaselinePrompt(data.content)
    },
    isDirty: screenIsDirty,
  })
  const baseChanged = configSeed.baseChanged || promptSeed.baseChanged

  function reloadFromServer() {
    configSeed.reload()
    promptSeed.reload()
  }

  const saveMutation = useMutation({
    mutationFn: (payload: ProductTypesFile) => putDraftFile<ProductTypesFile>('product_types', payload),
    onSuccess: (_result, payload) => {
      // Reset dirty state against the snapshot just saved (PRD 02 AC4) -
      // done directly here rather than waiting on the invalidated query's
      // async refetch, which would otherwise cause a false-dirty flicker.
      setDraft(payload)
      setSavedSnapshot(payload)
      configSeed.noteSaved(payload)
      refreshAfterWrite(queryClient, { kind: 'draft-file', fileKey: 'product_types' })
    },
  })

  if (
    productTypesQuery.isPending ||
    globalDefaultsQuery.isPending ||
    documentTypesQuery.isPending ||
    extractorsQuery.isPending
  ) {
    return <SkeletonBlock />
  }

  if (productTypesQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load product types">
        {errorMessage(productTypesQuery.error)}
      </Banner>
    )
  }
  if (globalDefaultsQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load platform defaults">
        {errorMessage(globalDefaultsQuery.error)}
      </Banner>
    )
  }
  if (documentTypesQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load document types">
        {errorMessage(documentTypesQuery.error)}
      </Banner>
    )
  }
  if (extractorsQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load extractors">
        {errorMessage(extractorsQuery.error)}
      </Banner>
    )
  }

  const existingProduct = productTypesQuery.data.product_types[productId]

  // Content failed to load - still let the operator type a replacement
  // rather than getting stuck; treat as if nothing was saved.
  if (existingProduct && savedBaselinePrompt === null && baselinePromptQuery.isError) {
    setSavedBaselinePrompt('')
  }

  if (!existingProduct && !initialEntry) {
    return (
      <EmptyState
        icon={FileWarning}
        headline="Product type not found"
        description="This product type doesn't exist in the current draft or live bundle."
        action={{ label: 'Back to Products', onClick: () => navigate('/product-types') }}
      />
    )
  }

  if (!draft) {
    return <SkeletonBlock />
  }

  const product = draft.product_types[productId]
  const isDirty = screenIsDirty()
  const fieldErrors = extractFieldErrors(saveMutation.error)

  const limitPathToField: Record<string, LimitField> = {
    [`product_types.${productId}.limits.max_documents`]: 'max_documents',
    [`product_types.${productId}.limits.max_total_pages`]: 'max_total_pages',
    [`product_types.${productId}.limits.max_pdf_size_mb`]: 'max_pdf_size_mb',
  }
  const limitServerErrors: Partial<Record<LimitField, string>> = {}
  fieldErrors?.forEach((err) => {
    const field = limitPathToField[err.path]
    if (field) limitServerErrors[field] = err.message
  })

  // The GUI backend doesn't actually validate output_resolution at draft-save
  // time (confirmed by reading gui_backend/core/validation.py), so this
  // rarely fires today - kept for the same reason limitServerErrors is: if a
  // check is ever added server-side, it surfaces here automatically rather
  // than silently disappearing into the generic banner.
  const outputResolutionServerErrors: Record<string, Record<string, string>> = {}
  const outputResolutionPathPrefix = `product_types.${productId}.output_resolution.`
  fieldErrors?.forEach((err) => {
    if (!err.path.startsWith(outputResolutionPathPrefix)) return
    const [key, subPath] = err.path.slice(outputResolutionPathPrefix.length).split('.')
    if (!key || !subPath) return
    outputResolutionServerErrors[key] = { ...outputResolutionServerErrors[key], [subPath]: err.message }
  })

  const outputResolutionIssues = resolveOutputResolutionForSave(
    product.output_resolution ?? {},
    outputKeyDrafts,
  )

  // Coverage warning (confirmed in-scope): a document-types field with no
  // matching output_resolution entry is silently never surfaced in the API
  // response - a real, documented past production incident
  // (CONFIG_REFERENCE.md), not currently caught by backend or GUI anywhere.
  // Checked against every document type the product could require
  // (hard_floor + must_have_all + every must_have_any_of group), since any
  // of them might be the one actually present on a given request.
  const requiredDocTypeIds = Array.from(
    new Set([
      ...(product.document_requirements.hard_floor ?? []),
      ...(product.document_requirements.rules?.must_have_all ?? []),
      ...(product.document_requirements.rules?.must_have_any_of ?? []).flat(),
    ]),
  )
  const outputResolutionKeys = new Set(Object.keys(product.output_resolution ?? {}))
  const requiredDocTypeFields = requiredDocTypeIds.flatMap(
    (docTypeId) => Object.keys(documentTypesQuery.data.document_types[docTypeId]?.fields ?? {}),
  )
  const unmappedDocumentFields = Array.from(
    new Set(requiredDocTypeFields.filter((field) => !outputResolutionKeys.has(field))),
  )
  // Real field names a "This document's field" priority entry can point at -
  // sourced from the product's own required document types, never typed by
  // hand (see PriorityList/PriorityEntryEditor).
  const documentFieldOptions = Array.from(new Set(requiredDocTypeFields))

  function updateProduct(updater: (product: ProductType) => ProductType) {
    setDraft((current) => {
      if (!current) return current
      return {
        ...current,
        product_types: {
          ...current.product_types,
          [productId]: updater(current.product_types[productId]),
        },
      }
    })
  }

  // Baseline Prompt content-then-reference save ordering (Frontend PRD 02
  // Core logic): a non-empty prompt is written to its own file *before* the
  // whole-file product_types PUT ever references it, so a content-write
  // failure can never leave a dangling reference. An emptied prompt unsets
  // the reference in that same whole-file PUT, then best-effort deletes the
  // now-orphaned file afterward - a delete failure never blocks or reverts
  // the save that already succeeded. The backend rejects empty PUT content
  // outright, so an emptied field always takes the delete path, never a PUT.
  async function handleSave() {
    if (!draft || !(await configSeed.ensureCurrent()) || !(await promptSeed.ensureCurrent())) return
    const current = draft.product_types[productId]
    const trimmedBaseline = baselinePrompt.trim()
    const previousFilename = current.extraction?.prompt_template_file
    const filename = deriveProductBaselinePromptFilename(productId)

    setBaselinePromptError(null)
    let nextExtraction = current.extraction

    if (trimmedBaseline.length > 0) {
      try {
        await putDraftPrompt(filename, baselinePrompt)
        // The prompt file is now written to the draft whatever the config
        // save below does, so refresh what shows it right away.
        refreshAfterWrite(queryClient, { kind: 'embedded-prompt', filename })
      } catch (error) {
        setBaselinePromptError(errorMessage(error) ?? 'Failed to save the baseline prompt')
        return
      }
      nextExtraction = { ...(current.extraction ?? { extractor_id: '' }), prompt_template_file: filename }
    } else if (previousFilename) {
      nextExtraction = { ...current.extraction, extractor_id: current.extraction?.extractor_id ?? '', prompt_template_file: undefined }
    }

    // Remaps any synthetic output_resolution key to the name typed into its
    // row (outputResolutionIssues, computed above from the same draft) - the
    // Save button is already disabled while emptyKeys/duplicateKeys is
    // non-empty, so `resolved` is always the complete, final map here.
    const payload: ProductTypesFile = {
      ...draft,
      product_types: {
        ...draft.product_types,
        [productId]: {
          ...current,
          extraction: nextExtraction,
          output_resolution: outputResolutionIssues.resolved,
        },
      },
    }
    saveMutation.mutate(payload, {
      onSuccess: () => {
        setSavedBaselinePrompt(baselinePrompt)
        if (trimmedBaseline.length === 0 && previousFilename) {
          void deleteDraftPrompt(previousFilename)
            .catch(() => {
              // Best-effort - an orphaned draft file is harmless (Frontend PRD
              // 02 Core logic step 3), never surfaced to the operator.
            })
            .finally(() => {
              // Refresh only once the delete has landed, or the refetch can
              // still see the file.
              refreshAfterWrite(queryClient, { kind: 'embedded-prompt', filename: previousFilename })
            })
        }
      },
    })
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <Link to="/product-types" className="text-text-secondary hover:text-text-primary">
          <ArrowLeft size={18} strokeWidth={1.5} />
        </Link>
        <h1 className="text-title font-semibold text-text-primary">{product.description ?? productId}</h1>
      </div>

      {baseChanged && <StaleDraftBanner onReload={reloadFromServer} />}

      {saveMutation.isError && (
        <Banner variant="danger" icon={AlertCircle} title="Save failed">
          {formatSaveError(saveMutation.error, fieldErrors)}
        </Banner>
      )}

      {baselinePromptError && (
        <Banner variant="danger" icon={AlertCircle} title="Failed to save baseline prompt">
          {baselinePromptError}
        </Banner>
      )}

      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
          Basic info
          <InfoTooltip text="The name shown for this product throughout the admin tool, and how it currently processes requests." />
        </h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-meta font-medium text-text-secondary" htmlFor="description">
              Description
            </label>
            <input
              id="description"
              type="text"
              value={product.description ?? ''}
              onChange={(event) => updateProduct((p) => ({ ...p, description: event.target.value }))}
              className="rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="flex items-center gap-1 text-meta font-medium text-text-secondary">
              Product type ID
              <InfoTooltip text="The permanent identifier callers use to request this product. It can't be changed after creation." />
            </span>
            <span className="font-mono text-body text-text-primary">{productId}</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="flex items-center gap-1 text-meta font-medium text-text-secondary">
              Mode
              <InfoTooltip text="How this product processes requests. Every product currently runs in sync mode, meaning it returns a response immediately." />
            </span>
            <span className="inline-flex w-fit items-center rounded-pill bg-surface-raised px-2 py-1 text-meta text-text-primary">
              {product.mode}
            </span>
          </div>
        </div>

        <label className="flex w-fit items-center gap-2 text-body text-text-primary">
          <input
            type="checkbox"
            checked={product.echo_original_documents ?? false}
            onChange={(event) => updateProduct((p) => ({ ...p, echo_original_documents: event.target.checked }))}
          />
          Echo original documents
          <InfoTooltip text="When enabled, the original uploaded document may be included in the response for documents of this product. This alone doesn't turn echoing on - the document type itself, and a platform-wide output setting, must also allow it." />
        </label>
        <p className="text-meta text-text-secondary">
          Requires the same setting to also be enabled on each required document type (Documents &gt; General
          tab) and in platform-wide output settings (Global Configs &gt; Limits &amp; Output).
        </p>
      </section>

      <DocumentRequirementsSection
        documentRequirements={product.document_requirements}
        onChange={(document_requirements) => updateProduct((p) => ({ ...p, document_requirements }))}
        onAddDocumentType={() => setModalOpen(true)}
      />

      <LimitsSection
        limits={product.limits}
        hardCaps={globalDefaultsQuery.data.hard_caps}
        onChange={(limits) => updateProduct((p) => ({ ...p, limits }))}
        serverErrors={limitServerErrors}
      />

      <ExtractionSection
        extraction={product.extraction}
        extractors={extractorsQuery.data.extractors}
        onChange={(extraction) => updateProduct((p) => ({ ...p, extraction }))}
        extractorDefaultPrompt={{
          content: extractorDefaultQuery.data?.content,
          isLoading: extractorDefaultQuery.isLoading,
          isError: extractorDefaultQuery.isError,
        }}
        baselinePrompt={baselinePrompt}
        onBaselinePromptChange={setBaselinePrompt}
      />

      {unmappedDocumentFields.length > 0 && (
        <Banner variant="warning" icon={AlertCircle} title="Some document fields have no output mapping">
          {unmappedDocumentFields.join(', ')} will be extracted but never appear in the response - add an
          Output Resolution entry for each, or confirm that's intentional.
        </Banner>
      )}

      <OutputResolutionSection
        entries={product.output_resolution ?? {}}
        onChange={(output_resolution) => updateProduct((p) => ({ ...p, output_resolution }))}
        keyDrafts={outputKeyDrafts}
        onKeyDraftChange={(key, name) => setOutputKeyDrafts((current) => ({ ...current, [key]: name }))}
        duplicateKeys={outputResolutionIssues.duplicateKeys}
        invalidKeys={outputResolutionIssues.invalidKeys}
        documentFieldOptions={documentFieldOptions}
        serverErrors={outputResolutionServerErrors}
      />

      <CaseDataRequirementsSection
        rules={product.case_data_requirements?.rules ?? {}}
        onChange={(rules) => updateProduct((p) => ({ ...p, case_data_requirements: { rules } }))}
      />

      <OrchestrationStepsSection steps={product.orchestration.steps} />

      <div className="flex justify-end">
        {(() => {
          const button = (
            <button
              type="button"
              disabled={
                !isDirty ||
                baseChanged ||
                saveMutation.isPending ||
                outputResolutionIssues.emptyKeys.length > 0 ||
                outputResolutionIssues.duplicateKeys.length > 0 ||
                outputResolutionIssues.invalidKeys.length > 0
              }
              onClick={() => void handleSave()}
              className="rounded-control bg-accent px-4 py-2 text-body font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
          )
          return baseChanged ? <Tooltip text={STALE_DRAFT_SAVE_REASON}>{button}</Tooltip> : button
        })()}
      </div>

      <AddDocumentTypeModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        documentTypeKeys={Object.keys(documentTypesQuery.data.document_types)}
        rules={product.document_requirements.rules ?? {}}
        onConfirm={(selectedKeys, group) => {
          updateProduct((p) => {
            if (group === 'hard_floor') {
              const hardFloor = p.document_requirements.hard_floor ?? []
              return {
                ...p,
                document_requirements: {
                  ...p.document_requirements,
                  hard_floor: Array.from(new Set([...hardFloor, ...selectedKeys])),
                },
              }
            }
            const rules = p.document_requirements.rules ?? {}
            const nextRules =
              group === 'must_have_all'
                ? {
                    ...rules,
                    must_have_all: Array.from(new Set([...(rules.must_have_all ?? []), ...selectedKeys])),
                  }
                : (() => {
                    const index = Number(group.split(':')[1])
                    const groups = rules.must_have_any_of ?? []
                    return {
                      ...rules,
                      must_have_any_of: groups.map((groupKeys, i) =>
                        i === index ? Array.from(new Set([...groupKeys, ...selectedKeys])) : groupKeys,
                      ),
                    }
                  })()
            return {
              ...p,
              document_requirements: { ...p.document_requirements, rules: nextRules },
            }
          })
        }}
      />
    </div>
  )
}
